import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import {
  Calendar,
  Clock,
  DollarSign,
  Mail,
  Send,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  User,
  Plane
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentRemindersTab() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [reminderStatus, setReminderStatus] = useState(null);
  const [lastSendResult, setLastSendResult] = useState(null);
  
  // Test email form
  const [testEmail, setTestEmail] = useState('');
  const [testDays, setTestDays] = useState(3);
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    fetchReminderStatus();
  }, []);

  const fetchReminderStatus = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/payment-reminders/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReminderStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch reminder status:', err);
      toast.error('Failed to load payment reminder status');
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerReminders = async () => {
    if (!window.confirm('Send payment reminders to all eligible customers now?')) return;
    
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/payment-reminders/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLastSendResult(res.data);
      toast.success(`Sent ${res.data.reminders_sent} reminder(s)`);
      fetchReminderStatus(); // Refresh status
    } catch (err) {
      console.error('Failed to send reminders:', err);
      toast.error('Failed to send payment reminders');
    } finally {
      setSending(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    setSendingTest(true);
    try {
      await axios.post(`${API}/admin/payment-reminders/test`, {
        email: testEmail,
        days_until_due: parseInt(testDays),
        name: 'Test Traveler',
        trip_title: 'Sample Trip',
        monthly_payment: 500,
        payments_made: 1,
        total_payments: 3,
        remaining_amount: 1000
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Test reminder sent to ${testEmail}`);
    } catch (err) {
      console.error('Failed to send test email:', err);
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const getDueBadge = (daysUntil) => {
    if (daysUntil < 0) {
      return <Badge className="bg-red-500 text-white">Overdue</Badge>;
    } else if (daysUntil === 0) {
      return <Badge className="bg-red-500 text-white">Due Today</Badge>;
    } else if (daysUntil <= 3) {
      return <Badge className="bg-orange-500 text-white">Due in {daysUntil}d</Badge>;
    } else if (daysUntil <= 7) {
      return <Badge className="bg-amber-500 text-white">Due in {daysUntil}d</Badge>;
    } else {
      return <Badge className="bg-blue-100 text-blue-800">Due in {daysUntil}d</Badge>;
    }
  };

  const getReminderBadges = (reminders) => {
    if (!reminders || Object.keys(reminders).length === 0) {
      return <span className="text-muted-foreground text-sm">No reminders sent</span>;
    }
    
    return (
      <div className="flex gap-1 flex-wrap">
        {reminders.day_7 && (
          <Badge className="bg-green-100 text-green-800 text-xs">
            <CheckCircle className="h-3 w-3 mr-1" />
            7-day
          </Badge>
        )}
        {reminders.day_3 && (
          <Badge className="bg-green-100 text-green-800 text-xs">
            <CheckCircle className="h-3 w-3 mr-1" />
            3-day
          </Badge>
        )}
        {reminders.day_0 && (
          <Badge className="bg-green-100 text-green-800 text-xs">
            <CheckCircle className="h-3 w-3 mr-1" />
            Day-of
          </Badge>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payment-reminders-tab">
      {/* Summary Cards */}
      <div className="grid sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Due Today</p>
                <p className="text-2xl font-bold text-red-600">{reminderStatus?.due_today || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <Clock className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Within 3 Days</p>
                <p className="text-2xl font-bold text-orange-600">{reminderStatus?.due_within_3_days || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Within 7 Days</p>
                <p className="text-2xl font-bold text-amber-600">{reminderStatus?.due_within_7_days || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Upcoming</p>
                <p className="text-2xl font-bold text-blue-600">{reminderStatus?.total_upcoming || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions Row */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-secondary" />
            Send Payment Reminders
          </CardTitle>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={fetchReminderStatus}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={handleTriggerReminders}
              disabled={sending}
              className="bg-secondary hover:bg-secondary/90"
              data-testid="send-reminders-btn"
            >
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Reminders Now
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-800">How Reminders Work</p>
                <p className="text-sm text-blue-700 mt-1">
                  Payment reminders are sent automatically at 7 days, 3 days, and the day of payment due date.
                  Each customer receives at most one reminder per interval. Click "Send Reminders Now" to manually
                  trigger reminders for all eligible payments.
                </p>
              </div>
            </div>
          </div>
          
          {/* Last send result */}
          {lastSendResult && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="font-medium text-green-800">Last Send Results</span>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Checked:</span>
                  <span className="ml-2 font-medium">{lastSendResult.checked}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Sent:</span>
                  <span className="ml-2 font-medium text-green-600">{lastSendResult.reminders_sent}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Errors:</span>
                  <span className="ml-2 font-medium text-red-600">{lastSendResult.errors}</span>
                </div>
              </div>
              {lastSendResult.details && lastSendResult.details.length > 0 && (
                <div className="mt-3 pt-3 border-t border-green-200">
                  <p className="text-sm text-muted-foreground mb-2">Reminders sent to:</p>
                  <div className="space-y-1">
                    {lastSendResult.details.map((d, i) => (
                      <div key={i} className="text-sm flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-green-600" />
                        <span>{d.email}</span>
                        <Badge className="text-xs">{d.days_until_due === 0 ? 'Due Today' : `${d.days_until_due} days`}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upcoming Payments Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-secondary" />
            Upcoming Payments
          </CardTitle>
        </CardHeader>
        <CardContent>
          {reminderStatus?.bookings?.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <DollarSign className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No upcoming payments</p>
              <p className="text-sm mt-1">All bookings are either paid in full or have no remaining balance</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium text-muted-foreground">Customer</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">Trip</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">Amount</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">Due Date</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">Status</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">Reminders Sent</th>
                  </tr>
                </thead>
                <tbody>
                  {reminderStatus?.bookings?.map((booking, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="font-medium">{booking.guest_name || 'Guest'}</p>
                            <p className="text-sm text-muted-foreground">{booking.guest_email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Plane className="h-4 w-4 text-muted-foreground" />
                          <span className="truncate max-w-[200px]">{booking.trip}</span>
                        </div>
                      </td>
                      <td className="p-3 font-medium text-green-600">
                        ${booking.amount?.toLocaleString()}
                      </td>
                      <td className="p-3">
                        {new Date(booking.due_date).toLocaleDateString()}
                      </td>
                      <td className="p-3">
                        {getDueBadge(booking.days_until_due)}
                      </td>
                      <td className="p-3">
                        {getReminderBadges(booking.reminders_sent)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Test Email Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-secondary" />
            Send Test Reminder Email
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="test-email">Email Address</Label>
              <Input
                id="test-email"
                type="email"
                placeholder="test@example.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="mt-1"
                data-testid="test-reminder-email"
              />
            </div>
            <div>
              <Label htmlFor="test-days">Days Until Due</Label>
              <select
                id="test-days"
                value={testDays}
                onChange={(e) => setTestDays(e.target.value)}
                className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background"
              >
                <option value={7}>7 days (upcoming)</option>
                <option value={3}>3 days (soon)</option>
                <option value={0}>0 days (due today)</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleSendTestEmail}
                disabled={sendingTest || !testEmail}
                className="w-full"
                variant="outline"
                data-testid="send-test-reminder-btn"
              >
                {sendingTest ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Test
                  </>
                )}
              </Button>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-3">
            Send a test reminder email to preview how the payment reminder looks. Uses mock booking data.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
